import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

const LoadingScreen = ({ onComplete }) => {
  const [progress, setProgress] = useState(0)
  const [showSub, setShowSub] = useState(false)
  const [showLoader, setShowLoader] = useState(false)
  const frameRef = useRef(null)
  const startRef = useRef(null)

  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      // Skip animation for reduced motion preferences
      setProgress(100)
      setShowSub(true)
      setShowLoader(true)
      const t = setTimeout(onComplete, 60)
      return () => clearTimeout(t)
    }

    startRef.current = Date.now()
    const duration = 2500

    const showSubTimer = setTimeout(() => setShowSub(true), 400)
    const showLoaderTimer = setTimeout(() => setShowLoader(true), 200)

    const animate = () => {
      const elapsed = Date.now() - startRef.current
      const pct = Math.min((elapsed / duration) * 100, 100)
      setProgress(pct)

      if (elapsed < duration) {
        frameRef.current = requestAnimationFrame(animate)
      } else {
        setTimeout(onComplete, 400)
      }
    }

    frameRef.current = requestAnimationFrame(animate)

    return () => {
      cancelAnimationFrame(frameRef.current)
      clearTimeout(showSubTimer)
      clearTimeout(showLoaderTimer)
    }
  }, [onComplete])

  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6, ease: 'easeInOut' }}
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center loading-overlay"
    >
      {/* Ambient glow */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="absolute left-1/2 top-1/2 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[100px]"
          style={{ backgroundColor: 'var(--accent)', opacity: 0.06 }}
        />
        <div
          className="absolute left-1/3 top-1/3 h-[300px] w-[300px] rounded-full blur-[80px]"
          style={{ backgroundColor: 'var(--accent)', opacity: 0.04 }}
        />
      </div>

      <div className="relative flex flex-col items-center">
        {/* Name */}
        <motion.h1
          initial={{ opacity: 0, y: 30, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl"
        >
          Omkar Kardel
        </motion.h1>

        {/* Subtitle */}
        <AnimatePresence>
          {showSub && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              className="mt-4 text-center"
            >
              <p className="text-lg text-blue-400 sm:text-xl">Full Stack MERN Developer</p>
              <p className="mt-1 text-sm text-slate-500">AWS & DevOps Learner</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Progress bar */}
        <AnimatePresence>
          {showLoader && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              className="mt-12 w-56 sm:w-64"
            >
              <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-slate-800">
                <motion.div
                  className="progress-shimmer h-full rounded-full"
                  style={{ width: `${progress}%` }}
                  transition={{ duration: 0.1, ease: 'linear' }}
                />
              </div>
              <div className="mt-3 flex items-center justify-between">
                <p className="text-xs text-slate-600">
                  Loading
                  <span className="dot-loader ml-1.5 inline-flex items-center gap-1">
                    <span /><span /><span />
                  </span>
                </p>
                <p className="text-xs font-medium text-blue-400/80">
                  {Math.round(progress)}%
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  )
}

export default LoadingScreen
